/**
 * Ethereum Gas Price Fetcher
 * Fetches current gas prices using public RPC endpoints
 * Falls back to Etherscan API if API key is provided
 */

export interface GasPrices {
  safe: number; // Gwei
  propose: number; // Gwei
  fast: number; // Gwei
  baseFee: number; // Gwei
  lastBlock: number;
}

// Public RPC endpoints for Ethereum mainnet
const ETH_RPC_ENDPOINTS = [
  'https://eth.llamarpc.com',
  'https://rpc.ankr.com/eth',
  'https://eth-mainnet.public.blastapi.io',
  'https://ethereum.publicnode.com',
];

/**
 * Fetch gas price from RPC using eth_gasPrice
 */
async function fetchGasPriceFromRPC(rpcUrl: string): Promise<number | null> {
  try {
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_gasPrice',
        params: [],
        id: 1,
      }),
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data.error || !data.result) {
      return null;
    }

    // Convert from wei to gwei
    const gasPriceWei = BigInt(data.result);
    const gasPriceGwei = Number(gasPriceWei) / 1e9;
    return gasPriceGwei;
  } catch (error) {
    return null;
  }
}

/**
 * Fetch fee history to get base fee and estimate gas prices
 */
async function fetchFeeHistoryFromRPC(rpcUrl: string): Promise<{ baseFee: number; gasUsedRatio: number } | null> {
  try {
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_feeHistory',
        params: ['0x1', 'latest', [25, 50, 75]], // Get last block with percentiles
        id: 1,
      }),
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data.error || !data.result) {
      return null;
    }

    const feeHistory = data.result;
    const baseFeeWei = BigInt(feeHistory.baseFeePerGas[feeHistory.baseFeePerGas.length - 1]);
    const baseFeeGwei = Number(baseFeeWei) / 1e9;

    // Calculate average gas used ratio
    const gasUsedRatios = feeHistory.gasUsedRatio || [];
    const avgGasUsedRatio = gasUsedRatios.length > 0
      ? gasUsedRatios.reduce((sum: number, val: string) => sum + parseFloat(val), 0) / gasUsedRatios.length
      : 0.5;

    return { baseFee: baseFeeGwei, gasUsedRatio: avgGasUsedRatio };
  } catch (error) {
    return null;
  }
}

/**
 * Fetch current gas prices using public RPC endpoints
 */
export async function fetchGasPrices(chainId: number = 1): Promise<GasPrices | null> {
  if (chainId !== 1) {
    console.warn('[GasOracle] Only Ethereum mainnet (chainId 1) is supported');
    return null;
  }

  // Try each RPC endpoint until one works
  for (const rpcUrl of ETH_RPC_ENDPOINTS) {
    try {
      const [gasPrice, feeHistory] = await Promise.all([
        fetchGasPriceFromRPC(rpcUrl),
        fetchFeeHistoryFromRPC(rpcUrl),
      ]);

      if (gasPrice && feeHistory) {
        // Estimate gas prices based on current gas price and base fee
        // Safe: base fee + 10% priority
        // Propose: base fee + 20% priority  
        // Fast: base fee + 50% priority
        const priorityFee = Math.max(0, gasPrice - feeHistory.baseFee);
        
        const safe = feeHistory.baseFee + (priorityFee * 0.5);
        const propose = feeHistory.baseFee + (priorityFee * 0.8);
        const fast = feeHistory.baseFee + (priorityFee * 1.5);

        // Get latest block number
        const blockResponse = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_blockNumber',
            params: [],
            id: 1,
          }),
          signal: AbortSignal.timeout(5000),
        });

        let lastBlock = 0;
        if (blockResponse.ok) {
          const blockData = await blockResponse.json();
          if (blockData.result) {
            lastBlock = parseInt(blockData.result, 16);
          }
        }

        console.log('[GasOracle] Successfully fetched gas prices from RPC:', rpcUrl);
        
        return {
          safe: Math.max(safe, feeHistory.baseFee * 1.1), // Ensure safe is at least 10% above base
          propose: Math.max(propose, feeHistory.baseFee * 1.2), // Ensure propose is at least 20% above base
          fast: Math.max(fast, feeHistory.baseFee * 1.5), // Ensure fast is at least 50% above base
          baseFee: feeHistory.baseFee,
          lastBlock,
        };
      }
    } catch (error) {
      console.warn(`[GasOracle] Failed to fetch from ${rpcUrl}:`, error);
      continue;
    }
  }

  console.error('[GasOracle] All RPC endpoints failed');
  return null;
}

/**
 * Format gas price for display (always in Gwei, matching Etherscan format)
 */
export function formatGasPrice(gwei: number): string {
  // Always display in Gwei, even for small values
  // Etherscan shows many decimal places for precision (e.g., 0.373954579 gwei)
  if (gwei < 0.01) {
    // For very small values, show up to 9 decimal places
    return `${gwei.toFixed(9)} Gwei`;
  } else if (gwei < 1) {
    // For values less than 1, show up to 6 decimal places
    return `${gwei.toFixed(6)} Gwei`;
  } else if (gwei < 10) {
    // For single digits, show 2 decimal places
    return `${gwei.toFixed(2)} Gwei`;
  } else {
    // For larger values, show 1 decimal place
    return `${gwei.toFixed(1)} Gwei`;
  }
}

/**
 * Get gas price label based on speed
 */
export function getGasPriceLabel(speed: 'safe' | 'propose' | 'fast'): string {
  switch (speed) {
    case 'safe':
      return 'Slow';
    case 'propose':
      return 'Standard';
    case 'fast':
      return 'Fast';
    default:
      return '';
  }
}

